const draftStates = new WeakMap();

export function selectAccountTab(tabs, panels, targetName) {
  const selectedTab = tabs.find((tab) => tab.dataset.accountTab === targetName);
  if (!selectedTab) return null;

  tabs.forEach((tab) => {
    const isSelected = tab === selectedTab;
    tab.setAttribute('aria-selected', String(isSelected));
    tab.tabIndex = isSelected ? 0 : -1;
    tab.classList?.toggle('is-active', isSelected);
  });
  panels.forEach((panel) => {
    panel.hidden = panel.dataset.accountPanel !== targetName;
  });
  return selectedTab;
}

export function getAccountTabFocusIndex(key, currentIndex, tabCount) {
  if (tabCount < 1) return currentIndex;
  if (key === 'Home') return 0;
  if (key === 'End') return tabCount - 1;
  if (key === 'ArrowRight' || key === 'ArrowDown') return (currentIndex + 1) % tabCount;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return (currentIndex - 1 + tabCount) % tabCount;
  return currentIndex;
}

export function getRequestedAccountView(locationLike = {}) {
  const params = new URLSearchParams(locationLike.search || '');
  const result = {};
  if (params.get('view') === 'orders') result.view = 'orders';
  if (result.view === 'orders' && params.get('tab') === 'draft-orders') result.tab = 'draft-orders';
  return result;
}

export function formatDraftMoney(money, locale) {
  const amount = Number(money?.amount);
  const currency = String(money?.currencyCode || 'USD');
  if (!Number.isFinite(amount)) return '';
  try {
    return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function normalizeDraftPage(value) {
  const page = value && typeof value === 'object' ? value : {};
  const orders = Array.isArray(page.orders) ? page.orders : [];
  return {
    orders: orders.map((order) => ({
      ...order,
      checkoutAvailable: order?.checkoutAvailable === true,
      lineItems: (Array.isArray(order?.lineItems) ? order.lineItems : []).map((line) => ({
        ...line,
        properties: (Array.isArray(line?.properties) ? line.properties : []).filter(
          (property) => property && !String(property.key || '').startsWith('_'),
        ),
      })),
    })),
    pageInfo: {
      hasNextPage: Boolean(page.pageInfo?.hasNextPage),
      endCursor: typeof page.pageInfo?.endCursor === 'string' ? page.pageInfo.endCursor : null,
    },
    totalCount: typeof page.totalCount === 'number' ? page.totalCount : null,
  };
}

export async function requestDraftCheckout(draftId, dependencies = {}) {
  const fetchFn = dependencies.fetchFn || window.fetch.bind(window);
  const response = await fetchFn('/apps/linendipity-addresses/drafts/checkout', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ draftId }),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.ok) {
    const error = new Error(
      response.status === 401
        ? 'Sign in again to proceed to checkout.'
        : response.status === 409
          ? 'This draft order can no longer proceed to checkout.'
          : result?.message || 'Checkout could not be opened. Please try again.',
    );
    error.status = response.status;
    throw error;
  }

  let checkoutUrl;
  try {
    checkoutUrl = new URL(result.checkoutUrl);
  } catch {
    throw new Error('Checkout could not be opened. Please try again.');
  }
  if (checkoutUrl.protocol !== 'https:') {
    throw new Error('Checkout could not be opened. Please try again.');
  }
  return checkoutUrl.href;
}

export async function proceedToDraftCheckout({
  draftId,
  button,
  status,
  request = requestDraftCheckout,
  navigate = (url) => window.location.assign(url),
  refresh = async () => {},
}) {
  if (!button || button.disabled) return false;

  button.disabled = true;
  button.textContent = 'Opening checkout…';
  if (status) {
    status.textContent = 'Opening Shopify checkout…';
    status.dataset.state = 'loading';
  }

  try {
    const checkoutUrl = await request(draftId);
    navigate(checkoutUrl);
    return true;
  } catch (error) {
    button.disabled = false;
    button.textContent = 'Place Order';
    const message = error?.message || 'Checkout could not be opened. Please try again.';
    if (status) {
      status.textContent = message;
      status.dataset.state = 'error';
    }
    if (error?.status === 409) await refresh({ announcement: message });
    return false;
  }
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function formatDraftDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return '';
  return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
}

function createDefinition(label, value) {
  const wrapper = document.createElement('div');
  wrapper.append(element('dt', '', label), element('dd', '', value));
  return wrapper;
}

function createLineItem(line) {
  const item = element('li', 'ld-account-hub__draft-line');
  const heading = element('div', 'ld-account-hub__draft-line-heading');
  heading.append(
    element('strong', '', String(line?.title || 'Item')),
    element('span', '', `Qty ${Number(line?.quantity) || 0}`),
  );
  item.append(heading);
  if (line?.variantTitle) item.append(element('p', '', String(line.variantTitle)));

  const pricing = element('p', 'ld-account-hub__draft-line-price');
  pricing.textContent = `${formatDraftMoney(line?.unitPrice)} each · ${formatDraftMoney(line?.totalPrice)}`;
  item.append(pricing);

  const properties = Array.isArray(line?.properties) ? line.properties : [];
  if (properties.length) {
    const list = element('dl', 'ld-account-hub__draft-properties');
    properties.forEach((property) => {
      list.append(createDefinition(String(property.key || ''), String(property.value || '')));
    });
    item.append(list);
  }
  return item;
}

function createDraftOrderCard(order, index, hub) {
  const card = element('article', 'ld-account-hub__draft-card');
  const heading = element('div', 'ld-account-hub__draft-summary');
  const titleGroup = element('div', 'ld-account-hub__draft-title');
  titleGroup.append(
    element('strong', '', String(order?.name || 'Draft order')),
    element('span', '', formatDraftDate(order?.createdAt)),
  );
  const meta = element('dl', 'ld-account-hub__draft-meta');
  meta.append(
    createDefinition('Status', String(order?.status || 'Open')),
    createDefinition('Items', String(Number(order?.itemCount) || 0)),
    createDefinition('Total', formatDraftMoney(order?.total)),
  );

  const detailsId = `draft-order-details-${String(order?.id || index).replace(/[^a-z0-9]+/gi, '-')}`;
  const toggle = element('button', 'ld-account-hub__draft-toggle', 'View details');
  toggle.type = 'button';
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-controls', detailsId);
  const actions = element('div', 'ld-account-hub__draft-card-actions');
  let checkoutStatus = null;
  if (order?.checkoutAvailable === true) {
    const statusId = `${detailsId}-checkout-status`;
    const checkout = element('button', 'ld-account-hub__draft-checkout', 'Place Order');
    checkout.type = 'button';
    checkout.setAttribute('aria-describedby', statusId);
    checkoutStatus = element('p', 'ld-account-hub__draft-checkout-status');
    checkoutStatus.id = statusId;
    checkoutStatus.setAttribute('aria-live', 'polite');
    checkoutStatus.setAttribute('aria-atomic', 'true');
    checkout.addEventListener('click', () => {
      void proceedToDraftCheckout({
        draftId: order.id,
        button: checkout,
        status: checkoutStatus,
        refresh: (options) => refreshDraftOrders(hub, {}, options),
      });
    });
    actions.append(checkout);
  }
  actions.append(toggle);
  heading.append(titleGroup, meta, actions);

  const details = element('div', 'ld-account-hub__draft-details');
  details.id = detailsId;
  details.hidden = true;
  const lines = element('ul', 'ld-account-hub__draft-lines');
  (Array.isArray(order?.lineItems) ? order.lineItems : []).forEach((line) => lines.append(createLineItem(line)));
  details.append(lines);
  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    toggle.textContent = expanded ? 'View details' : 'Hide details';
    details.hidden = expanded;
  });

  card.append(heading);
  if (checkoutStatus) card.append(checkoutStatus);
  card.append(details);
  return card;
}

function setDraftStatus(hub, message, state) {
  const status = hub.querySelector('[data-draft-orders-status]');
  if (!status) return;
  status.textContent = message;
  status.dataset.state = state;
}

function showDraftEmptyState(list) {
  const empty = element('div', 'ld-account-hub__empty-state');
  empty.append(
    element('h2', '', 'No draft orders yet'),
    element('p', '', 'Draft orders you save from the cart will appear here.'),
  );
  list.append(empty);
}

export async function loadDraftOrders(hub, cursor, dependencies = {}) {
  const state = draftStates.get(hub) || {
    loaded: false,
    loading: false,
    nextCursor: null,
    failedCursor: null,
  };
  if (state.loading || (!cursor && state.loaded)) return false;
  draftStates.set(hub, state);

  const list = hub.querySelector('[data-draft-orders-list]');
  const retry = hub.querySelector('[data-draft-orders-retry]');
  const more = hub.querySelector('[data-draft-orders-more]');
  const count = hub.querySelector('[data-draft-orders-count]');
  const fetchFn = dependencies.fetchFn || window.fetch.bind(window);
  if (!list || !retry || !more) return false;

  state.loading = true;
  retry.hidden = true;
  more.hidden = true;
  setDraftStatus(hub, cursor ? 'Loading more draft orders…' : 'Loading draft orders…', 'loading');
  if (!cursor && !state.loaded) list.replaceChildren();

  try {
    const params = new URLSearchParams({ first: '10' });
    if (cursor) params.set('after', cursor);
    const response = await fetchFn(`/apps/linendipity-addresses/drafts?${params}`, {
      headers: { Accept: 'application/json' },
    });
    const result = await response.json().catch(() => null);
    if (draftStates.get(hub) !== state) return false;
    if (!response.ok || !result?.ok) {
      const message =
        response.status === 401
          ? 'Sign in again to view your draft orders.'
          : result?.message || 'Draft orders could not be loaded. Please try again.';
      throw Object.assign(new Error(message), { status: response.status });
    }

    const page = normalizeDraftPage(result);
    if (!cursor) list.replaceChildren();
    page.orders.forEach((order, index) => list.append(createDraftOrderCard(order, index, hub)));
    if (!cursor && page.orders.length === 0) showDraftEmptyState(list);
    if (page.totalCount !== null && count) count.textContent = String(page.totalCount);

    state.loaded = true;
    state.failedCursor = null;
    state.nextCursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
    more.hidden = !state.nextCursor;
    more.dataset.cursor = state.nextCursor || '';
    setDraftStatus(
      hub,
      state.nextCursor ? 'More draft orders are available.' : 'All draft orders are shown.',
      page.orders.length ? 'loaded' : 'empty',
    );
    return true;
  } catch (error) {
    if (draftStates.get(hub) !== state) return false;
    state.failedCursor = cursor || null;
    retry.hidden = false;
    setDraftStatus(hub, error?.message || 'Draft orders could not be loaded. Please try again.', 'error');
    return false;
  } finally {
    if (draftStates.get(hub) === state) state.loading = false;
  }
}

export async function refreshDraftOrders(hub, dependencies = {}, options = {}) {
  const list = hub?.querySelector?.('[data-draft-orders-list]');
  if (!hub || !list) return false;
  draftStates.delete(hub);
  list.replaceChildren();
  const refreshed = await loadDraftOrders(hub, undefined, dependencies);
  if (refreshed && options.announcement) {
    setDraftStatus(hub, options.announcement, 'error');
    hub.querySelector('#account-draft-orders-tab')?.focus?.();
  }
  return refreshed;
}

function tabParts(hub, group) {
  if (!group) return { tabs: [], panels: [] };
  const tabs = Array.from(group.querySelectorAll('[role="tab"][data-account-tab]'));
  const targetNames = new Set(tabs.map((tab) => tab.dataset.accountTab));
  const panels = Array.from(hub.querySelectorAll('[role="tabpanel"][data-account-panel]')).filter((panel) =>
    targetNames.has(panel.dataset.accountPanel),
  );
  return { tabs, panels };
}

function activateTab(hub, group, targetName) {
  const { tabs, panels } = tabParts(hub, group);
  const selected = selectAccountTab(tabs, panels, targetName);
  if (selected?.dataset.accountTab === 'draft-orders') loadDraftOrders(hub);
  return selected;
}

function initializeTabGroup(hub, group) {
  const { tabs, panels } = tabParts(hub, group);
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activateTab(hub, group, tab.dataset.accountTab));
    tab.addEventListener('keydown', (event) => {
      const targetIndex = getAccountTabFocusIndex(event.key, index, tabs.length);
      if (targetIndex === index && !['Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      activateTab(hub, group, tabs[targetIndex].dataset.accountTab)?.focus();
    });
  });
  const initiallySelected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
  if (initiallySelected) selectAccountTab(tabs, panels, initiallySelected.dataset.accountTab);
}

export function initializeAccountHub(hub) {
  const tabGroups = Array.from(hub.querySelectorAll('[data-account-tabs]'));
  tabGroups.forEach((group) => initializeTabGroup(hub, group));

  hub.querySelectorAll('[data-account-open-orders]').forEach((control) => {
    control.addEventListener('click', () => {
      const primaryGroup = hub.querySelector('[data-account-tabs="primary"]');
      activateTab(hub, primaryGroup, 'orders')?.focus();
    });
  });
  hub.querySelector('[data-draft-orders-retry]')?.addEventListener('click', () => {
    const state = draftStates.get(hub);
    loadDraftOrders(hub, state?.failedCursor || undefined);
  });
  hub.querySelector('[data-draft-orders-more]')?.addEventListener('click', () => {
    const state = draftStates.get(hub);
    if (state?.nextCursor) loadDraftOrders(hub, state.nextCursor);
  });

  const requested = getRequestedAccountView(window.location);
  if (requested.view === 'orders') {
    activateTab(hub, hub.querySelector('[data-account-tabs="primary"]'), 'orders');
  }
  if (requested.tab) {
    activateTab(hub, hub.querySelector('[data-account-tabs="orders"]'), requested.tab);
  }
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-account-hub]').forEach(initializeAccountHub);
}
